import {queries} from '@/shared/integrations/zero'
import {ListCategoriesInput} from '@cypro/engine/queries'
import {DrugsCategory} from '@cypro/engine/schema'
import {type GetPageQuery, type GetSingleQuery} from '@cypro/ui/utils/zero-virtualizer'
import {useCallback, useMemo, useState} from 'react'

export type CategoryStartRow = Pick<DrugsCategory, 'id' | 'createdAt' | 'name'>

export function useGetInfiniteDrugsCategories({
  sort = {dir: 'asc', field: 'name'},
  filters,
  limit = 25,
}: Omit<Partial<ListCategoriesInput>, 'cursor'> = {}) {
  const [search, setSearch] = useState('')

  const listContextParams = useMemo(
    () => ({sort, filters: {status: ['active'] as const, ...filters}, limit, search}),
    // oxlint-disable-next-line react-hooks/exhaustive-deps
    [sort?.dir, sort?.field, filters?.search, filters?.status, limit, search]
  )

  // @ts-expect-error schema type mismatch (works at runtime)
  const getPageQuery: GetPageQuery<DrugsCategory, CategoryStartRow> = useCallback(
    ({limit: pageLimit, start, dir}) => {
      const cursor =
        start?.id && start?.createdAt
          ? {
              id: start.id,
              direction: dir,
              createdAt: start.createdAt,
              name: start.name,
            }
          : undefined

      return {
        query: queries.basicData.listDrugsCategories({
          cursor,
          limit: pageLimit,
          sort,
          filters: {status: ['active'] as const, ...filters, search: search || filters?.search},
        }),
      }
    },
    // oxlint-disable-next-line react-hooks/exhaustive-deps
    [listContextParams]
  )

  // @ts-expect-error schema type mismatch (works at runtime)
  const getSingleQuery: GetSingleQuery<DrugsCategory> = useCallback(
    ({id}) => ({
      query: queries.basicData.getDrugCategoryById(id),
    }),
    []
  )

  const toStartRow = useCallback(
    (row: DrugsCategory): CategoryStartRow => ({
      id: row.id,
      createdAt: row.createdAt,
      name: row.name,
    }),
    []
  )

  const transformToSelectItem = useCallback((row: DrugsCategory) => ({label: row.name, value: row.id}), [])

  return {
    search,
    onSearchChange: setSearch,
    getPageQuery,
    getSingleQuery,
    toStartRow,
    listContextParams,
    transformToSelectItem,
  }
}
